import * as tslib_1 from "tslib";
import axios from "axios";
/**
 * AI 大模型流式对话接口（云枢二开 ext.ai 模块）。
 * 后端在此接口中完成模型调用、鉴权、审计和敏感信息处理，浏览器不得直接保存模型密钥。
 * 接口文档见：AI接口文档.md
 *
 * 使用工程默认 axios 实例请求：鉴权头（Authorization: Bearer <token>）、代理/API 地址
 * 重写、参数加密等均由工程 axios 拦截器（config/axios.ts）统一处理，与工程其他接口一致。
 */
/** AI 大模型流式对话接口的默认地址（完整路径，与文档一致）。 */
export const AI_POLISH_API_URL = "/api/ai/chat-stream";
/**
 * 调用 AI 大模型流式对话接口（SSE），并将增量文本实时回调给调用方。
 *
 * 本接口为 POST，不能用 EventSource（仅支持 GET）。浏览器端 axios 不支持
 * responseType: "stream"，因此通过 onDownloadProgress 读取 XHR 的 responseText
 * 累积文本，按行增量解析 OpenAI 标准 SSE 增量格式。
 *
 * @param url 接口地址，默认使用 AI_POLISH_API_URL
 * @param params 请求参数（description / prompt 必填）
 * @param onDelta 每个增量文本块的回调，用于逐块渲染
 * @returns 完整拼接后的文本
 */
export function chatStream(url, params, onDelta) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        let result = "";
        let pending = "";
        let consumed = 0;
        let finished = false;
        let streamError = "";
        /** 处理新增的响应文本（responseText 为累积全文，只处理增量部分）。 */
        const processText = (text) => {
            if (finished || text.length <= consumed) {
                return;
            }
            pending += text.slice(consumed);
            consumed = text.length;
            const lines = pending.split("\n");
            // 保留可能未完整的一行，等待下一次数据块
            pending = lines.pop() || "";
            for (const line of lines) {
                const event = parseSseLine(line);
                if (event.error) {
                    streamError = event.error;
                    finished = true;
                    return;
                }
                if (event.done) {
                    finished = true;
                    return;
                }
                if (event.delta) {
                    result += event.delta;
                    onDelta(event.delta);
                }
            }
        };
        try {
            const response = yield axios.post(url, params, {
                responseType: "text",
                onDownloadProgress: (progressEvent) => {
                    if (finished) {
                        return;
                    }
                    // responseText 为累积全文；收到 error 事件或 [DONE] 后停止处理，
                    // 由服务端关闭流（load 事件）结束本次请求（axios 0.18 下 abort 会使 Promise 无法结束）。
                    processText(progressEvent.currentTarget.responseText || "");
                },
            });
            // 未触发进度回调或末尾残留内容（响应整体到达 / 服务端关闭流时）一并处理
            processText((response && response.data) || "");
        }
        catch (error) {
            // 网络错误 / 非 2xx / 超时等异常；error 事件按错误抛出，[DONE] 后的收尾异常视为正常结束
            if (streamError) {
                throw new Error(streamError);
            }
            if (finished) {
                return result;
            }
            throw error;
        }
        if (streamError) {
            throw new Error(streamError);
        }
        return result;
    });
}
/**
 * 解析单条 SSE 行（data: ...）。
 * 响应为 OpenAI 标准增量格式，并兼容 error 事件
 * （event: error + data: {"error": {...}}，HTTP 状态码仍为 200）。
 */
function parseSseLine(line) {
    if (!line.startsWith("data:")) {
        return {};
    }
    const data = line.slice(5).trim();
    if (!data) {
        return {};
    }
    if (data === "[DONE]") {
        return { done: true };
    }
    let payload;
    try {
        payload = JSON.parse(data);
    }
    catch (e) {
        // 非 JSON 数据行（如注释、空行），忽略
        return {};
    }
    if (payload.error) {
        return {
            error: (payload.error && (payload.error.message || payload.error.msg)) ||
                "AI 接口返回未知错误。",
        };
    }
    const choice = payload.choices && payload.choices[0];
    const delta = choice && choice.delta && choice.delta.content;
    return {
        delta: typeof delta === "string" ? delta : "",
        finishReason: choice ? choice.finish_reason : null,
    };
}
